
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

interface TabItem {
  title: string;
  description: string;
}

interface InnovationTabsProps {
  items: TabItem[];
}

const InnovationTabs: React.FC<InnovationTabsProps> = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 4, md: 8 },
        maxWidth: '1200px',
        mx: 'auto',
      }}
    >
      {/* Sidebar de Tabs */}
      <Box
        sx={{
          flex: { md: '0 0 350px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <Box
              key={index}
              onClick={() => setActiveIndex(index)}
              sx={{
                p: 3,
                cursor: 'pointer',
                borderLeft: `2px solid ${isActive ? '#B8C5B8' : 'rgba(184, 197, 184, 0.1)'}`,
                background: isActive
                  ? 'linear-gradient(90deg, rgba(184, 197, 184, 0.05) 0%, transparent 100%)'
                  : 'transparent',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(184, 197, 184, 0.02)',
                  borderLeftColor: isActive ? '#B8C5B8' : 'rgba(184, 197, 184, 0.4)',
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: isActive ? '#FFFFFF' : '#B8C5B8',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  letterSpacing: '0.02em',
                  transition: 'color 0.3s ease',
                }}
              >
                {item.title}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Área de Contenido con Animación */}
      <Box sx={{ flex: 1, position: 'relative', minHeight: { xs: '300px', md: 'auto' } }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ width: '100%', height: '100%' }}
          >
            <Box
              sx={{
                p: { xs: 4, md: 6 },
                background: 'rgba(36, 44, 39, 0.4)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(184, 197, 184, 0.15)',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.75rem', lg: '2rem' },
                  fontWeight: 300,
                  color: '#D4E0D4',
                  lineHeight: 1.6,
                  letterSpacing: '-0.01em',
                }}
              >
                {items[activeIndex].description}
              </Typography>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default InnovationTabs;
